import * as React from 'react';
import * as classnames from 'classnames';
import * as _ from 'lodash';
import { AudienceRuleOption } from '@nexxt/common/types';
import Select from 'react-select';
import { UilTrashAlt } from '@iconscout/react-unicons';
import { MAPPING_TARGETTING_OPTIONS } from '../../../../../constants/audience';

interface NumericAudienceRuleOptionProps {
    systemGenerated?: boolean;
    option: AudienceRuleOption;
    index: number;
    isDisabled: boolean;
    quotaEnabled: boolean;
    errorObject: {
        min?: { message: string; isInvalid: boolean };
        max?: { message: string; isInvalid: boolean };
    };
    onEditOptions: (
        index,
        newValue: { keyName: string; value: unknown },
        toggle: boolean
    ) => void;
    onChangeTargetingOption: (
        index: number,
        mode: { terminateEnabled: boolean; quotaEnabled: boolean }
    ) => void;
    onDeleteOption?: (index: number) => void;
    onSampleSizeChange: (index: number, event: number) => void;
}

const NumericAudienceRuleOption = ({
    systemGenerated,
    option,
    index,
    isDisabled,
    quotaEnabled,
    errorObject,
    onEditOptions,
    onChangeTargetingOption,
    onDeleteOption,
    onSampleSizeChange
}: NumericAudienceRuleOptionProps): JSX.Element => {
    const [showDeleteBtn, setShowDeleteBtn] = React.useState<boolean>(false);
    const [showMinWarning, setShowMinWarning] = React.useState<boolean>(
        !!option.id || false
    );
    const [showMaxWarning, setShowMaxWarning] = React.useState<boolean>(
        !!option.id || false
    );

    const selectDropdownStyles = {
        control: (base) => ({
            ...base,
            background: '#F2F2F2'
        })
    };
    const hideDeleteBtn = index < 0; // auto generated options

    return (
        <tr
            className="option-row age"
            key={`age-${option.value}`}
            onMouseEnter={() =>
                !isDisabled && !hideDeleteBtn && setShowDeleteBtn(true)
            }
            onMouseLeave={() =>
                !isDisabled && !hideDeleteBtn && setShowDeleteBtn(false)
            }
        >
            {systemGenerated ? (
                <td colSpan={2}>{option.label}</td>
            ) : (
                <>
                    <td>
                        <input
                            type="number"
                            name="min"
                            className={classnames(`form-group__control`, {
                                'error-border':
                                    showMinWarning &&
                                    errorObject?.min?.isInvalid
                            })}
                            value={option.range.min}
                            onChange={(e) =>
                                onEditOptions(
                                    index,
                                    {
                                        keyName: 'range',
                                        value: {
                                            max: option.range.max,
                                            min: parseFloat(e.target.value)
                                        }
                                    },
                                    quotaEnabled
                                )
                            }
                            defaultValue=""
                            disabled={isDisabled}
                            onBlur={() => setShowMinWarning(true)}
                        />
                        {showMinWarning && errorObject?.min?.isInvalid ? (
                            <p className="range--invalid--message">
                                {errorObject?.min?.message}
                            </p>
                        ) : null}
                    </td>

                    <td>
                        <input
                            type="number"
                            name="max"
                            className={classnames(`form-group__control`, {
                                'error-border':
                                    showMaxWarning &&
                                    errorObject?.max?.isInvalid
                            })}
                            value={option.range.max}
                            onChange={(e) =>
                                onEditOptions(
                                    index,
                                    {
                                        keyName: 'range',
                                        value: {
                                            max: parseFloat(e.target.value),
                                            min: option.range.min
                                        }
                                    },
                                    quotaEnabled
                                )
                            }
                            defaultValue=""
                            disabled={isDisabled}
                            onBlur={() => setShowMaxWarning(true)}
                        />
                        {showMaxWarning && errorObject?.max?.isInvalid ? (
                            <p className="range--invalid--message">
                                {errorObject?.max?.message}
                            </p>
                        ) : null}
                    </td>
                </>
            )}
            <td
                style={{
                    display: !quotaEnabled ? 'flex' : '',
                    marginRight: showDeleteBtn && !quotaEnabled && '-3px'
                }}
            >
                <div
                    style={{
                        width: '100%'
                    }}
                >
                    <Select
                        options={MAPPING_TARGETTING_OPTIONS}
                        value={
                            option.mode.terminateEnabled
                                ? MAPPING_TARGETTING_OPTIONS[1]
                                : MAPPING_TARGETTING_OPTIONS[0]
                        }
                        onChange={(response) => {
                            const terminateEnabled =
                                response === MAPPING_TARGETTING_OPTIONS[0]
                                    ? false
                                    : true;
                            onChangeTargetingOption(index, {
                                ...option.mode,
                                terminateEnabled
                            });
                        }}
                        isDisabled={isDisabled}
                        styles={selectDropdownStyles}
                        components={{
                            IndicatorSeparator: () => null
                        }}
                        isSearchable={false}
                    />
                </div>
                {!quotaEnabled && showDeleteBtn ? (
                    <div className="delete-container">
                        <div
                            className="float-delete"
                            onClick={() => {
                                setShowDeleteBtn(false);
                                onDeleteOption(index);
                            }}
                        >
                            <UilTrashAlt size="15px" />
                        </div>
                    </div>
                ) : null}
            </td>
            {quotaEnabled && (
                <td
                    align="right"
                    style={{ display: 'flex', justifyContent: 'end' }}
                >
                    {quotaEnabled && !option.mode.terminateEnabled && (
                        <input
                            type="number"
                            name="quota"
                            min={0}
                            className={classnames(`form-group__control `, {
                                'error-border': !option.sampleSize.max
                            })}
                            value={option.sampleSize.max}
                            onChange={(e) =>
                                onSampleSizeChange(
                                    index,
                                    Number(e.target.value)
                                )
                            }
                            defaultValue=""
                            disabled={isDisabled}
                            style={{
                                marginRight: showDeleteBtn && '-3px'
                            }}
                        />
                    )}
                    {showDeleteBtn ? (
                        <div className="delete-container">
                            <div
                                className="float-delete"
                                onClick={() => {
                                    setShowDeleteBtn(false);
                                    onDeleteOption(index);
                                }}
                            >
                                <UilTrashAlt size="15px" />
                            </div>
                        </div>
                    ) : null}
                </td>
            )}
        </tr>
    );
};

export default NumericAudienceRuleOption;
